<template>
  <transition name="workspacesFade">
    <div 
      v-if="show" 
      class="workspaces-modal-backdrop" 
      @click="closeModal"
    >
      <transition name="workspacesSlide">
        <div 
          v-if="show" 
          class="workspaces-modal-dialog"
          @click.stop
        >
          <div class="workspaces-modal-content">
            <!-- Header -->
            <div class="workspaces-modal-header">
              <h5 class="workspaces-modal-title">
                <b-icon icon="building" class="mr-2"></b-icon>
                {{ $t('workspaces') }}
              </h5>
              <button 
                type="button" 
                class="workspaces-close" 
                @click="closeModal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <!-- Body -->
            <div class="workspaces-modal-body">
              <div class="workspaces-grid">
                <div
                  v-for="workspace in workspaces"
                  :key="workspace.uuid"
                  class="workspace-card"
                  :class="{ 'workspace-active': isCurrentWorkspace(workspace) }"
                  @click="selectWorkspace(workspace)"
                >
                  <div class="workspace-card-body">
                    <div class="workspace-icon">
                      <b-icon 
                        :icon="isCurrentWorkspace(workspace) ? 'check-circle-fill' : 'building'" 
                        :variant="isCurrentWorkspace(workspace) ? 'success' : 'primary'"
                        font-scale="2"
                      ></b-icon>
                    </div>
                    <div class="workspace-info">
                      <h6 class="workspace-name">{{ workspace.name }}</h6>
                      <p class="workspace-status" v-if="isCurrentWorkspace(workspace)">
                        <b-badge variant="success">{{ $t('current') }}</b-badge>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="workspaces-modal-footer">
              <b-button 
                variant="outline-primary" 
                @click="editWorkspaces"
              >
                <b-icon icon="pencil" class="mr-1"></b-icon>
                {{ $t('editWorkspaces') }}
              </b-button>
              <b-button 
                variant="secondary" 
                @click="closeModal"
              >
                {{ $t('close') }}
              </b-button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'WorkspacesModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    workspaces() {
      const project = this.$store.getters.getCurrentProject
      const workspaces = project ? project.workspaces || [] : []
      
      // Remove duplicates by UUID
      const uniqueWorkspaces = []
      const seenUuids = new Set()
      
      for (const workspace of workspaces) {
        if (workspace && workspace.uuid && !seenUuids.has(workspace.uuid)) {
          seenUuids.add(workspace.uuid)
          uniqueWorkspaces.push(workspace)
        }
      }
      
      return uniqueWorkspaces
    },
    currentWorkspace() {
      return this.$store.getters.getCurrentWorkspace
    }
  },
  methods: {
    closeModal() {
      this.$emit('close')
    },
    isCurrentWorkspace(workspace) {
      const current = this.currentWorkspace
      return current && workspace.uuid === current.uuid
    },
    selectWorkspace(workspace) {
      if (!this.isCurrentWorkspace(workspace)) {
        this.$store.commit("SET_CURRENT_WORKSPACE", workspace)
        this.$emit('workspaceChanged', workspace)
        this.closeModal()
      }
    },
    editWorkspaces() {
      this.$router.push('/admin/workspace')
      this.closeModal()
    }
  }
}
</script>

<style>
.workspaces-modal-backdrop {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background-color: rgba(0, 0, 0, 0.7) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 999999 !important;
}

.workspaces-modal-dialog {
  background: white !important;
  border-radius: 8px !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3) !important;
  max-width: 600px !important;
  width: 90% !important;
  max-height: 80vh !important;
  display: flex !important;
  flex-direction: column !important;
  z-index: 1000000 !important;
}

.workspaces-modal-content {
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
}

.workspaces-modal-header {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 1.25rem !important;
  border-bottom: 1px solid #e9ecef !important;
}

.workspaces-modal-title {
  margin: 0 !important;
  font-size: 1.25rem !important;
  font-weight: 500 !important;
  display: flex !important;
  align-items: center !important;
}

.workspaces-close {
  background: none !important;
  border: none !important;
  font-size: 1.5rem !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  color: var(--text-primary) !important;
  opacity: 0.5 !important;
  cursor: pointer !important;
  padding: 0 !important;
  width: 1.5rem !important;
  height: 1.5rem !important;
}

.workspaces-close:hover {
  opacity: 0.75 !important;
}

.workspaces-modal-body {
  padding: 1.25rem !important;
  overflow-y: auto !important;
  flex: 1 !important;
}

.workspaces-grid {
  display: grid !important;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)) !important;
  gap: 1rem !important;
}

.workspace-card {
  border: 2px solid #e9ecef !important;
  border-radius: 8px !important;
  padding: 1.25rem !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  background: white !important;
}

.workspace-card:hover {
  border-color: #007bff !important;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15) !important;
  transform: translateY(-2px) !important;
}

.workspace-card.workspace-active {
  border-color: #28a745 !important;
  background-color: #f8fff9 !important;
}

.workspace-card-body {
  display: flex !important;
  align-items: center !important;
  gap: 1rem !important;
}

.workspace-icon {
  flex-shrink: 0 !important;
}

.workspace-info {
  flex: 1 !important;
  min-width: 0 !important;
}

.workspace-name {
  margin: 0 !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  color: #212529 !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

.workspace-status {
  margin: 0.25rem 0 0 0 !important;
}

.workspaces-modal-footer {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 1rem 1.25rem !important;
  border-top: 1px solid #e9ecef !important;
}

/* Animations */
.workspacesFade-enter-active,
.workspacesFade-leave-active {
  transition: opacity 0.2s ease !important;
}

.workspacesFade-enter,
.workspacesFade-leave-to {
  opacity: 0 !important;
}

.workspacesSlide-enter-active,
.workspacesSlide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease !important;
}

.workspacesSlide-enter,
.workspacesSlide-leave-to {
  transform: translateY(-50px) !important;
  opacity: 0 !important;
}

/* Dark mode support */
.dark-mode .workspaces-modal-dialog {
  background: var(--form-bg) !important;
}

.dark-mode .workspaces-modal-header,
.dark-mode .workspaces-modal-footer {
  border-color: var(--border-default) !important;
}

.dark-mode .workspaces-modal-title,
.dark-mode .workspace-name {
  color: var(--text-primary) !important;
}

.dark-mode .workspaces-close {
  color: var(--text-primary) !important;
}

.dark-mode .workspace-card {
  background: var(--bg-card) !important;
  border-color: var(--border-default) !important;
}

.dark-mode .workspace-card:hover {
  border-color: #007bff !important;
  background: #2a3038 !important;
}

.dark-mode .workspace-card.workspace-active {
  border-color: #28a745 !important;
  background-color: #1a2e1f !important;
}
</style>
